/* @jsx h */
import { defineComponent, h } from "vue";
import {
  resolvePassViews,
  validateWalletPassDesign,
  type WalletDesignIssue,
} from "@lemmonhq/blogic/wallet-design";
import { previewBindings } from "../document";
import type { WalletPassDesign } from "../types";
import { contrastRatio } from "./panels/StylePanel";

/**
 * Save-blocking errors come from the shared schema (the API rejects the same
 * document). Warnings flag what renders poorly: low contrast, fields that are
 * empty for the sample, images that no longer exist, and a Google header that
 * will fall back to the program name.
 */
export const designChecks = (
  design: WalletPassDesign,
): { errors: WalletDesignIssue[]; warnings: string[] } => {
  const result = validateWalletPassDesign(design.kind, design.document);
  if (!result.success) return { errors: result.issues, warnings: [] };
  const { document } = design;
  const warnings: string[] = [];
  const contrast = contrastRatio(
    document.brand.colors.foreground,
    document.brand.colors.background,
  );
  if (contrast < 4.5) {
    warnings.push(
      `Field values have ${contrast.toFixed(1)}:1 contrast; aim for at least 4.5:1.`,
    );
  }
  for (const [slot, ref] of Object.entries(document.brand.assets)) {
    if (ref && !design.asset_urls[ref.asset_id]) {
      warnings.push(`The ${slot} image no longer exists. Pick another one.`);
    }
  }
  const views = resolvePassViews({
    kind: design.kind,
    document,
    bindings: previewBindings(design.kind),
    assetUrls: design.asset_urls,
  });
  const placed = new Set([
    ...Object.values(document.apple.rows).flat(),
    ...document.google.rows.flat(),
    document.google.header,
    ...(document.google.subheader ? [document.google.subheader] : []),
    ...document.google.modules,
    ...document.browser.secondary,
    ...(document.browser.primary ? [document.browser.primary] : []),
  ]);
  for (const field of document.content.fields) {
    const name = field.label || field.key;
    if (field.value.trim() === "") {
      warnings.push(`"${name}" has no value and is never shown.`);
    } else if (!placed.has(field.key)) {
      warnings.push(`"${name}" is not placed on any platform.`);
    }
  }
  if (document.google.enabled && !views.google.header) {
    warnings.push(
      "The Google header field is empty; Google will show the program name.",
    );
  }
  if (
    document.content.links.some((link) => !views.apple.links.find((l) => l.key === link.key))
  ) {
    warnings.push(
      "Links must start with https://, tel: or mailto: — others are left out.",
    );
  }
  return { errors: [], warnings };
};

export const DesignChecks = defineComponent<{ design: WalletPassDesign }>({
  name: "DesignChecks",
  props: ["design"],
  setup(props) {
    return () => {
      const { errors, warnings } = designChecks(props.design);
      if (errors.length === 0 && warnings.length === 0) return null;
      return (
        <div class="flex flex-col gap-2 w-full">
          {errors.length > 0 && (
            <div class="alert alert-error text-xs py-2 items-start">
              <span class="material-symbols-outlined text-base">error</span>
              <ul class="list-disc pl-4">
                {errors.slice(0, 6).map((issue) => (
                  <li key={`${issue.path}:${issue.message}`}>
                    {issue.path ? `${issue.path}: ` : ""}
                    {issue.message}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {warnings.length > 0 && (
            <div class="alert alert-warning text-xs py-2 items-start">
              <span class="material-symbols-outlined text-base">warning</span>
              <ul class="list-disc pl-4">
                {warnings.map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      );
    };
  },
});
